import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { ChartLineUp, House, Wallet, Robot, ListMagnifyingGlass, Star, SignOut, Lightning, Trophy, EnvelopeSimple, PlugsConnected } from "@phosphor-icons/react";

const links = [
  { to: "/dashboard", label: "Dashboard", icon: House, testId: "nav-dashboard" },
  { to: "/trade", label: "Trade", icon: ChartLineUp, testId: "nav-trade" },
  { to: "/positions", label: "Positions", icon: Wallet, testId: "nav-positions" },
  { to: "/bots", label: "Auto Bots", icon: Robot, testId: "nav-bots" },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, testId: "nav-leaderboard" },
  { to: "/history", label: "History", icon: ListMagnifyingGlass, testId: "nav-history" },
  { to: "/watchlist", label: "Watchlist", icon: Star, testId: "nav-watchlist" },
  { to: "/notifications", label: "Alerts", icon: EnvelopeSimple, testId: "nav-notifications", badgeKind: "sentiment_flip" },
  { to: "/broker", label: "Broker", icon: PlugsConnected, testId: "nav-broker" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Iter 82 — Notification Badge. Poll the /notifications/unread-count
  // endpoint (filtered to sentiment_flip) so the sidebar dot lights up
  // when a fresh flip alert lands. Clears when the user visits the
  // /notifications page — that page calls POST /notifications/mark-read
  // for the sentiment_flip kind on mount.
  const [sentimentFlipUnread, setSentimentFlipUnread] = useState(0);
  // Iter 88 batch-3 — Ops Shift Handoff. Map of `actor_id → {email, at}`
  // for admins currently viewing an admin surface. Entries expire after
  // 60s of no heartbeat so the pill self-drains when a peer navigates away.
  const [coViewers, setCoViewers] = useState({});
  // Iter 84 — Badge Aria Announce. Track the previous count so we
  // only announce when the number *grows* (fresh alerts arrive) —
  // mark-read decreases go unannounced to avoid noise.
  const [ariaAnnouncement, setAriaAnnouncement] = useState("");
  // Iter 87 — Debounce Config. Snapshot the resolved (clamped) value
  // on mount so the aria-live region carries a `data-debounce-ms`
  // attribute — lets Playwright + Jest verify the localStorage
  // override without touching component internals.
  const [debounceMs] = useState(() => {
    try {
      const raw = localStorage.getItem("stratos.aria.debounce-ms");
      if (raw != null && raw !== "") {
        const parsed = parseInt(raw, 10);
        if (Number.isFinite(parsed)) {
          return Math.min(3000, Math.max(100, parsed));
        }
      }
    } catch {}
    return 500;
  });
  useEffect(() => {
    let cancelled = false;
    let prevCount = 0;
    // Iter 85 — Aria Announce Delay. Debounce the aria-live update
    // so a burst of 3 polls in the same tick produces a single
    // announcement (latest count wins). The 500ms window is well
    // below the mean screen-reader flush interval yet long enough
    // to coalesce ticks that fire in quick succession.
    // Iter 87 — Debounce Config. Power users can tune the window
    // via ``localStorage['stratos.aria.debounce-ms']`` to match
    // their screen-reader flush pace. Clamped 100..3000ms so a
    // fat-fingered "50000" doesn't silently drop announcements.
    // The resolved value already lives in state (``debounceMs``) so
    // the aria-live region can expose it as a data attribute.
    let pendingPhrase = null;
    let debounceTimer = null;
    const flush = () => {
      if (!cancelled && pendingPhrase) {
        setAriaAnnouncement(`${pendingPhrase} · ${Date.now()}`);
        pendingPhrase = null;
      }
    };
    const poll = () => {
      api.get("/notifications/unread-count?kind=sentiment_flip")
        .then((r) => {
          if (cancelled) return;
          const n = r.data?.count ?? 0;
          setSentimentFlipUnread(n);
          if (n > prevCount) {
            pendingPhrase = n === 1
              ? "1 new sentiment alert"
              : `${n} new sentiment alerts`;
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(flush, debounceMs);
          }
          prevCount = n;
        })
        .catch(() => {});
    };
    poll();
    const t = setInterval(poll, 30000);
    // Iter 86 — Debounce Coalesce. Also subscribe to SSE so
    // sentiment_flip events immediately re-poll (piggy-backs on
    // the 500ms debounce so 3 rapid events collapse to one
    // announcement).
    let es = null;
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/events/following`;
      es = new EventSource(url, { withCredentials: true });
      es.addEventListener("sentiment_flip", () => poll());
      // Iter 87 batch-4 — rate_limit_spike SSE. Admin-only, but we
      // subscribe unconditionally; the backend only publishes to
      // admin user_ids so non-admins see nothing. Fires a toast
      // instantly so on-call ops don't wait 30s for the poll.
      es.addEventListener("rate_limit_spike", (e) => {
        try {
          const data = JSON.parse(e.data || "{}");
          if (data.message) {
            import("sonner").then(({ toast }) => {
              // Iter 87 batch-5 — Spike Toast Deep-Link. Clicking
              // the toast dispatches a custom event picked up by
              // RateLimitDashboardCard, which scrolls the row into
              // view and flashes it for 1200ms. Navigates to
              // /notifications first if we're on any other route.
              toast.error(data.message, {
                description: `${data.bucket} · +${data.delta} rejects · tap to focus`,
                duration: 8000,
                action: {
                  label: "Focus",
                  onClick: () => {
                    if (typeof window !== "undefined") {
                      if (window.location.pathname !== "/notifications") {
                        window.location.href = "/notifications#rate-limits";
                        // The dashboard picks up the hash on mount
                        // and flashes the row automatically via the
                        // stratos:rate-limit-focus event below.
                      }
                      window.dispatchEvent(
                        new CustomEvent("stratos:rate-limit-focus", {
                          detail: { bucket: data.bucket },
                        })
                      );
                    }
                  },
                },
              });
            });
          }
        } catch {}
      });
      // Iter 88 batch-2 — Threshold Diff Notifications. Backend
      // only publishes threshold_change events to admins OTHER than
      // the actor, so a click here shows an "Ops shift alert" toast
      // within a few ms of a peer's edit. No SSE fan-out for the
      // actor — they already saw the success toast on their own PUT.
      // Iter 88 batch-3 — Ops Shift Handoff. Peer admins publish a
      // heartbeat every 30s while viewing the Rate-Limit dashboard.
      // We track them in a map keyed by actor_id so a stale entry
      // gets refreshed instead of duplicating the pill.
      es.addEventListener("ops_heartbeat", (e) => {
        try {
          const data = JSON.parse(e.data || "{}");
          if (!data.actor_id) return;
          setCoViewers((prev) => ({
            ...prev,
            [data.actor_id]: {
              email: data.actor_email || data.actor_id.slice(0, 6),
              page: data.page || "rate-limits",
              at: Date.now(),
            },
          }));
        } catch {}
      });
      es.addEventListener("threshold_change", (e) => {
        try {
          const data = JSON.parse(e.data || "{}");
          if (data.message) {
            import("sonner").then(({ toast }) => {
              const target = data.scope === "per_bucket"
                ? (data.bucket || "unknown")
                : "global";
              const detail = data.to == null
                ? `${target} · cleared override`
                : data.from == null
                ? `${target} · new override → ${data.to}`
                : `${target} · ${data.from} → ${data.to}`;
              toast(data.message, {
                description: detail,
                duration: 6000,
              });
            });
          }
        } catch {}
      });
    } catch {}
    return () => {
      cancelled = true;
      clearInterval(t);
      if (debounceTimer) clearTimeout(debounceTimer);
      if (es) try { es.close(); } catch {}
    };
  }, [debounceMs]);
  // Clear the badge locally the moment the user opens /notifications so
  // it feels responsive without waiting for the poll.
  useEffect(() => {
    if (location.pathname === "/notifications" && sentimentFlipUnread > 0) {
      setSentimentFlipUnread(0);
    }
  }, [location.pathname, sentimentFlipUnread]);

  // Iter 88 batch-3 — co-viewer TTL drain. Peers heartbeat every 30s;
  // we prune anything older than 60s so a peer that navigated away
  // disappears from the pill within one heartbeat interval.
  useEffect(() => {
    const t = setInterval(() => {
      setCoViewers((prev) => {
        const now = Date.now();
        const next = {};
        let changed = false;
        Object.entries(prev).forEach(([id, v]) => {
          if (now - v.at < 60000) next[id] = v;
          else changed = true;
        });
        return changed ? next : prev;
      });
    }, 10000);
    return () => clearInterval(t);
  }, []);
  const coViewerList = Object.entries(coViewers)
    .filter(([, v]) => Date.now() - v.at < 60000);

  return (
    <aside className="w-56 border-r border-zinc-800 bg-zinc-950 flex flex-col h-screen sticky top-0">
      {/* Iter 84 — Badge Aria Announce. Screen-reader-only live
          region so AT users hear a spoken announcement when fresh
          sentiment alerts land. `aria-live="polite"` avoids
          interrupting the user; timestamps in the value force a
          re-announcement even when the count is unchanged from a
          previous read. */}
      <div
        data-testid="sentiment-flip-aria-live"
        data-debounce-ms={debounceMs}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {ariaAnnouncement.split(" · ")[0]}
      </div>
      <div className="p-5 border-b border-zinc-800">
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-logo">
          <Lightning size={20} weight="fill" className="text-[#00C805]" />
          <div>
            <div className="heading text-lg font-black leading-none tracking-tighter">STRATOS</div>
            <div className="mono text-[10px] text-zinc-500 mt-1">/ AUTO TRADE</div>
          </div>
        </Link>
      </div>

      <nav className="flex-1 p-3 space-y-1">
        {links.map((l) => {
          const showBadge = l.badgeKind === "sentiment_flip" && sentimentFlipUnread > 0;
          // Iter 88 batch-3 — Ops Shift Handoff. Show a small pill on
          // the Alerts nav item when at least one peer admin is on
          // the Rate-Limit dashboard. Title carries the email list
          // so hovering surfaces exactly who's co-viewing.
          const showCoViewers = l.testId === "nav-notifications" && coViewerList.length > 0;
          return (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testId}
              data-badge={showBadge ? "true" : "false"}
              data-coviewers={showCoViewers ? coViewerList.length : "0"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                  isActive
                    ? "bg-zinc-900 text-white border-l-2 border-[#00C805]"
                    : "text-zinc-400 hover:bg-zinc-900 hover:text-white border-l-2 border-transparent"
                }`
              }
            >
              <l.icon size={16} weight="regular" />
              <span>{l.label}</span>
              {showBadge && (
                <span
                  data-testid={`nav-badge-${l.badgeKind}`}
                  data-count={sentimentFlipUnread}
                  className="ml-auto inline-flex items-center gap-1"
                  title={`${sentimentFlipUnread} unread sentiment flip alert${sentimentFlipUnread === 1 ? "" : "s"}`}
                  aria-label={`${sentimentFlipUnread} unread sentiment flip alert${sentimentFlipUnread === 1 ? "" : "s"}`}
                >
                  {/* Iter 83 — Unread Count Chip. Single alert keeps
                      the pulsing dot (no digit noise); >1 shows a
                      numeric pill so admins gauge volume at a glance.
                      Caps at 99+ so the chip stays narrow. */}
                  {sentimentFlipUnread === 1 ? (
                    <span
                      data-testid={`nav-badge-${l.badgeKind}-dot`}
                      className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"
                    />
                  ) : (
                    <span
                      data-testid={`nav-badge-${l.badgeKind}-count`}
                      className="mono text-[9px] leading-none px-1.5 py-[2px] rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-200 tabular-nums font-semibold"
                    >
                      {sentimentFlipUnread > 99 ? "99+" : sentimentFlipUnread}
                    </span>
                  )}
                </span>
              )}
              {showCoViewers && (
                <span
                  data-testid="nav-coviewers"
                  data-count={coViewerList.length}
                  className={`${showBadge ? "" : "ml-auto"} inline-flex items-center gap-1`}
                  title={`${coViewerList.length} peer${coViewerList.length === 1 ? "" : "s"} on ops · ${coViewerList.map(([, v]) => v.email).join(", ")}`}
                  aria-label={`${coViewerList.length} peer admin${coViewerList.length === 1 ? "" : "s"} viewing rate-limit dashboard`}
                >
                  <span
                    data-testid="nav-coviewers-pill"
                    className="mono text-[9px] leading-none px-1.5 py-[2px] rounded-full bg-cyan-500/15 border border-cyan-500/40 text-cyan-200 tabular-nums font-semibold flex items-center gap-1"
                  >
                    <span className="w-1 h-1 rounded-full bg-cyan-300 animate-pulse" />
                    {coViewerList.length}
                  </span>
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-3 border-t border-zinc-800">
        <div className="px-3 py-2 mb-2">
          <div className="text-[10px] text-zinc-500 mono uppercase tracking-widest">Signed in</div>
          <div className="text-sm text-zinc-200 truncate" data-testid="sidebar-user-email">{user?.email}</div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-sm transition-colors"
        >
          <SignOut size={16} />
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
}
